import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Clock,
  Pause,
  ShieldAlert,
  Stethoscope,
  Users,
} from 'lucide-react';
import type { EstadoSala, ModoAsignacion, Sala } from '../../types';
import { useReglasHorario } from '../../hooks/useReglasHorario';
import {
  isSalaAbiertaAhora,
  minutosHastaCierre,
} from '../../utils/horariosSalaUtil';
import styles from './IndicadoresSala.module.css';

export interface IndicadoresSalaProps {
  sala: Sala;
  /** Patients currently in the queue (override if you already computed it) */
  pacientesEnEspera?: number;
  /** Optional pre-computed wait time in minutes */
  estimadoEsperaMin?: number;
  /** Compact mode: single row of small chips (for topbar / cards) */
  compact?: boolean;
}

interface StateMeta {
  label: string;
  variant: 'open' | 'closed' | 'paused' | 'blocked';
  Icon: typeof Clock;
}

const STATE_META: Record<EstadoSala, StateMeta> = {
  abierta:    { label: 'Abierta',    variant: 'open',    Icon: Clock },
  cerrada:    { label: 'Cerrada',    variant: 'closed',  Icon: Clock },
  pausa:      { label: 'En pausa',   variant: 'paused',  Icon: Pause },
  bloqueada:  { label: 'Bloqueada',  variant: 'blocked', Icon: ShieldAlert },
  emergencia: { label: 'Emergencia', variant: 'blocked', Icon: AlertTriangle },
};

const MODE_LABEL: Record<ModoAsignacion, string> = {
  manual:     'Manual',
  automatico: 'Automático',
  hibrido:    'Híbrido',
};

/**
 * Live operational indicators for a sala: state, queue size, active doctors,
 * available slots, mode of assignment, and a countdown to closing time.
 *
 * Used on SalasPage cards and as a header strip in ColaPacientesPage.
 */
export function IndicadoresSala({
  sala,
  pacientesEnEspera,
  estimadoEsperaMin,
  compact = false,
}: IndicadoresSalaProps) {
  const [, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  // V2: cargar reglas horarias de la sala desde subcolección
  const { reglas } = useReglasHorario(sala.id);

  // Derive operational state
  const estado: EstadoSala = useMemo(() => {
    if (!sala.activa) return 'cerrada';
    if (sala.estado) return sala.estado;
    // Fallback inference V2
    if (!isSalaAbiertaAhora(reglas)) return 'cerrada';
    return 'abierta';
  }, [sala.activa, sala.estado, reglas]);

  const stateMeta = STATE_META[estado];
  const StateIcon = stateMeta.Icon;

  const espera = pacientesEnEspera ?? sala.pacientesEnEspera ?? 0;
  // Médicos EN LÍNEA (latido < 2 min), no el roster `medicosActivos`: ese
  // arreglo solo se vacía si el médico pulsa "Pausar", así que quien cierra
  // el navegador queda ahí días (en prod: 9 en el roster, 4 conectados, y el
  // dashboard mostraba 4). `medicosEnTurno` lo escribe el cron
  // reconciliarCuposEnVivo cada minuto con la misma regla del dashboard; el
  // roster queda solo como respaldo mientras el cron no haya escrito.
  const medicosActivos = typeof sala.medicosEnTurno === 'number'
    ? sala.medicosEnTurno
    : Array.isArray(sala.medicosActivos)
      ? sala.medicosActivos.length
      : 0;
  const modo = sala.modoAsignacion ?? 'hibrido';

  // Minutos hasta cierre del bloque actual (V2)
  const closeIn = useMemo(() => minutosHastaCierre(reglas), [reglas]);

  // Capacity available (best-effort calculation)
  const cuposDisponibles = useMemo(() => {
    if (sala.capacidad?.cuposDisponibles != null) {
      return sala.capacidad.cuposDisponibles;
    }
    if (medicosActivos === 0) return 0;
    // Issue #19: orden canónico (igual que minutosPorConsulta() en el backend,
    // functions/src/utils/salaCapacidad.ts) — `tiempoConsultaDefault` primero.
    // Antes este cálculo probaba `tiempoConsultaMin` primero e ignoraba
    // `tiempoConsultaDefault` por completo: si una sala tenía un
    // `tiempoConsultaMin` viejo (alias legacy) junto con un
    // `tiempoConsultaDefault`/`tiempoEstimadoMin` ya actualizado desde
    // SalaFormPage, este indicador seguía mostrando capacidad calculada con
    // el valor VIEJO.
    const tConsulta = sala.tiempoConsultaDefault ?? sala.tiempoConsultaMin ?? sala.tiempoEstimadoMin ?? 15;
    if (closeIn === Infinity || closeIn <= 0) return 0;
    return Math.max(
      0,
      Math.floor(medicosActivos * (closeIn / tConsulta)) - espera,
    );
  }, [sala, medicosActivos, espera, closeIn]);

  const closeSoon = estado === 'abierta' && closeIn !== Infinity && closeIn > 0 && closeIn <= 30;

  if (compact) {
    return (
      <div className={styles.compactRow} role="group" aria-label="Estado de la sala">
        <span className={`${styles.chip} ${styles[stateMeta.variant]}`}>
          <StateIcon size={11} aria-hidden="true" />
          {stateMeta.label}
        </span>
        <span className={styles.chipNeutral} title="Pacientes en espera">
          <Users size={11} aria-hidden="true" />
          {espera}
        </span>
        <span className={styles.chipNeutral} title="Médicos activos">
          <Stethoscope size={11} aria-hidden="true" />
          {medicosActivos}
        </span>
        {closeSoon && (
          <span className={styles.chipWarning} title="Cierra pronto">
            <Clock size={11} aria-hidden="true" />
            {closeIn} min
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={styles.panel} role="group" aria-label="Indicadores de sala">
      <div className={styles.row}>
        <div className={styles.cell}>
          <span className={styles.cellLabel}>Estado</span>
          <span className={`${styles.cellValue} ${styles[stateMeta.variant]}`}>
            <StateIcon size={13} aria-hidden="true" />
            {stateMeta.label}
          </span>
        </div>

        <div className={styles.cell}>
          <span className={styles.cellLabel}>En espera</span>
          <span className={styles.cellNum}>
            {espera}
            <span className={styles.cellUnit}>
              {espera === 1 ? 'paciente' : 'pacientes'}
            </span>
          </span>
        </div>

        <div className={styles.cell}>
          <span className={styles.cellLabel}>Médicos activos</span>
          <span className={styles.cellNum}>
            {medicosActivos}
            <span className={styles.cellUnit}>
              {medicosActivos === 1 ? 'disponible' : 'disponibles'}
            </span>
          </span>
        </div>

        <div className={styles.cell}>
          <span className={styles.cellLabel}>Cupos disponibles</span>
          <span
            className={`${styles.cellNum} ${cuposDisponibles === 0 ? styles.numWarning : ''}`}
          >
            {cuposDisponibles}
            <span className={styles.cellUnit}>
              {cuposDisponibles === 1 ? 'cupo' : 'cupos'}
            </span>
          </span>
        </div>

        <div className={styles.cell}>
          <span className={styles.cellLabel}>Modo</span>
          <span className={styles.cellMode}>{MODE_LABEL[modo]}</span>
        </div>
      </div>

      {estimadoEsperaMin != null && estimadoEsperaMin > 0 && (
        <div className={styles.banner}>
          <Clock size={13} aria-hidden="true" />
          <span>
            Tiempo de espera estimado: <strong>{estimadoEsperaMin} min</strong>
          </span>
        </div>
      )}

      {closeSoon && (
        <div className={`${styles.banner} ${styles.bannerWarning}`}>
          <Clock size={13} aria-hidden="true" />
          <span>
            La sala cierra en <strong>{closeIn} minutos</strong>. No se aceptan
            nuevos pacientes durante el buffer de cierre.
          </span>
        </div>
      )}
    </div>
  );
}

/* Helpers V1 removidos. La lógica de horario ahora vive en
   `utils/horariosSalaUtil.ts` y se consume vía `useReglasHorario`. */
